import { useState, useEffect, useRef, useCallback } from 'react';

export default function useInfiniteScroll(fetchPage, deps) {
  var postsState = useState([]);
  var posts = postsState[0];
  var setPosts = postsState[1];

  var cursorState = useState(null);
  var cursor = cursorState[0];
  var setCursor = cursorState[1];

  var hasMoreState = useState(true);
  var hasMore = hasMoreState[0];
  var setHasMore = hasMoreState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var loadingMoreState = useState(false);
  var loadingMore = loadingMoreState[0];
  var setLoadingMore = loadingMoreState[1];

  var sentinelRef = useRef(null);

  // Refs so the observer callback always reads current values
  var cursorRef = useRef(null);
  var loadingMoreRef = useRef(false);
  var hasMoreRef = useRef(true);
  var fetchPageRef = useRef(fetchPage);

  useEffect(
    function () {
      cursorRef.current = cursor;
    },
    [cursor]
  );
  useEffect(
    function () {
      loadingMoreRef.current = loadingMore;
    },
    [loadingMore]
  );
  useEffect(
    function () {
      hasMoreRef.current = hasMore;
    },
    [hasMore]
  );
  useEffect(
    function () {
      fetchPageRef.current = fetchPage;
    },
    [fetchPage]
  );

  var reset = useCallback(function () {
    setPosts([]);
    setCursor(null);
    cursorRef.current = null;
    setHasMore(true);
    hasMoreRef.current = true;
    setLoading(true);
    setLoadingMore(false);
    loadingMoreRef.current = false;
  }, []);

  // Initial load
  useEffect(function () {
    reset();
    fetchPage(null)
      .then(function (result) {
        setPosts(Array.isArray(result.posts) ? result.posts : []);
        var newCursor = result.nextCursor || null;
        setCursor(newCursor);
        cursorRef.current = newCursor;
        var more = !!result.hasMore;
        setHasMore(more);
        hasMoreRef.current = more;
        setLoading(false);
      })
      .catch(function () {
        setLoading(false);
        setHasMore(false);
        hasMoreRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // Intersection Observer for loading more -- stable, uses refs
  useEffect(
    function () {
      var node = sentinelRef.current;
      if (!node) return;

      var observer = new IntersectionObserver(
        function (entries) {
          if (entries[0].isIntersecting && !loadingMoreRef.current && hasMoreRef.current) {
            setLoadingMore(true);
            loadingMoreRef.current = true;
            fetchPageRef
              .current(cursorRef.current)
              .then(function (result) {
                var newPosts = Array.isArray(result.posts) ? result.posts : [];
                setPosts(function (prev) {
                  return prev.concat(newPosts);
                });
                var newCursor = result.nextCursor || null;
                setCursor(newCursor);
                cursorRef.current = newCursor;
                var more = !!result.hasMore;
                setHasMore(more);
                hasMoreRef.current = more;
                setLoadingMore(false);
                loadingMoreRef.current = false;
              })
              .catch(function () {
                setLoadingMore(false);
                loadingMoreRef.current = false;
              });
          }
        },
        { rootMargin: '200px' }
      );

      observer.observe(node);
      return function () {
        observer.disconnect();
      };
    },
    [loading]
  );

  var removePost = useCallback(function (postId) {
    setPosts(function (prev) {
      return prev.filter(function (p) {
        return p.id !== postId;
      });
    });
  }, []);

  return {
    posts: posts,
    loading: loading,
    loadingMore: loadingMore,
    hasMore: hasMore,
    sentinelRef: sentinelRef,
    removePost: removePost,
  };
}
